import type {ComponentType, FC} from 'react'
import {Redirect} from 'react-router-dom'
import type {RouteComponentProps} from 'react-router-dom'

import {TENANT_NO_STORAGE_RESIDENCY_REGION} from 'definitions/tenantConfig'
import {useAnalyticsTenantDomainGate} from 'features/Analytics/hooks'
import {useTenantConfigQuery} from 'hooks/useTenantConfigQuery'
import {AnalyticsRoutesEnum} from 'interfaces'

/**
 * AI Usage route: redirect when the primary domain is blocked, or residency is
 * confirmed no-storage. Stay unmounted (return null) until the first tenant
 * config attempt settles so AI usage queries do not fire before residency is
 * known. After an error, keep the page mounted even if the shared query
 * refetches (for example from AnalyticsRouteDropDown).
 */
export function withAnalyticsAIUsageRouteGuard<P extends RouteComponentProps>(
  Wrapped: ComponentType<P>,
): FC<P> {
  const Guarded: FC<P> = props => {
    const {isAnalyticsDisabledForPrimaryDomain} = useAnalyticsTenantDomainGate()
    const tenantConfigQuery = useTenantConfigQuery()
    const isTenantConfigPending =
      !tenantConfigQuery.isSuccess && !tenantConfigQuery.isError
    const isNoStorageTenantResidency =
      tenantConfigQuery.isSuccess &&
      tenantConfigQuery.data?.data?.residencyRegion ===
        TENANT_NO_STORAGE_RESIDENCY_REGION

    if (isTenantConfigPending) {
      return null
    }

    if (isAnalyticsDisabledForPrimaryDomain || isNoStorageTenantResidency) {
      return <Redirect to={AnalyticsRoutesEnum.ANALYTICS_OVERVIEW} />
    }
    return <Wrapped {...props} />
  }
  const wrappedName = Wrapped.displayName ?? Wrapped.name ?? 'Component'
  Guarded.displayName = `withAnalyticsAIUsageRouteGuard(${wrappedName})`
  return Guarded
}
